import * as React from 'react';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import { Chip, FormHelperText, IconButton, Stack, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { useLazyGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { AutoCompleteTags } from 'pages/AddDevice';
import { useTheme } from '@mui/material/styles';
import {
    useCreateTagMutation,
    useGetTagsQuery,
    useUpdateMultipleTagsMutation,
} from '@fiji/common/src/features/TagManagement/tagManagementApi';
import { setDeviceSideBar, setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { EditDevicesSkeleton } from './EditDevicesSkeleton';
import { useTranslation } from 'react-i18next';
import { CustomTransComponent, GroupPath } from 'components';
import { gray } from '@brightlayer-ui/colors';

import type { JSX } from 'react';

type Props = {
    onEditDeviceCancel: () => void;
    selectedDevices: any;
};

/**
 * Recursively sums the tag count across all devices
 */
const sumArray = (arr: any): number => {
    if (!arr || arr.length === 0) return 0;
    const [first, ...rest] = arr;
    return (first?.tags?.length ?? 0) + sumArray(rest);
};

/**
 * Checks if a tag is not in the current device's tag list
 * @param payload - Updated device payload
 * @param tagId - Tag ID to check
 * @param deviceId - Device ID to check
 * @returns True if tag is NOT in device's tags
 */
const isTagNotInDevice = (payload: any[], tagId: string, deviceId: string): boolean => {
    const deviceTags = payload?.find((payloadDevice: any) => deviceId === payloadDevice?.deviceId)?.tags ?? [];
    return !deviceTags.includes(tagId);
};

/**
 * Removes a tag from a specific device's payload
 * @param payload - Current payload
 * @param tagId - Tag ID to remove
 * @param deviceId - Device ID
 * @returns Updated payload with tag removed
 */
const removeTagFromDevice = (payload: any[], tagId: string, deviceId: string): any[] => {
    const payloadClone = JSON.parse(JSON.stringify(payload));
    const deviceIndex = payloadClone?.findIndex((item: any) => item.deviceId === deviceId);

    if (deviceIndex !== -1 && payloadClone[deviceIndex]?.tags) {
        const tagIndex = payloadClone[deviceIndex].tags.indexOf(tagId);
        if (tagIndex !== -1) {
            payloadClone[deviceIndex].tags.splice(tagIndex, 1);
        }
    }

    return payloadClone;
};

/**
 * Merges newly selected tags into all devices in payload
 * @param payload - Current payload
 * @param selectedTags - New tags to add
 * @returns Updated payload with new tags merged into all devices
 */
const mergeNewTagsIntoPayload = (payload: any[], selectedTags: any[]): any[] => {
    let newPayload = JSON.parse(JSON.stringify(payload));

    selectedTags.forEach((tag: any) => {
        newPayload = newPayload.map((device: any) => {
            // Skip if device already has this tag
            if (device?.tags?.includes(tag?.id)) {
                return device;
            }

            // Add new tag to device
            return {
                ...device,
                tags: [...(device?.tags ?? []), tag?.id],
            };
        });
    });

    return newPayload;
};

/**
 * Determines if keyboard input should be treated as tag creation
 * (i.e., not from arrow key navigation)
 */
const isTagCreationKeyPress = (keyCode: string): boolean => keyCode === 'Enter';

const canAddTag = (selectedTags: any[], tagValue: string): boolean => selectedTags.length < 20 && Boolean(tagValue);

const getTagRecords = (tagList: any): any[] => {
    const { data = {} } = tagList ?? {};
    return data.records ?? [];
};

const findTagByName = (tagRecords: any[], tagValue: string): any =>
    tagRecords.find((tag: any) => tag.name === tagValue);

const getCreatedTag = (response: any): any => {
    const { error, data = {} } = response ?? {};
    if (error) {
        return undefined;
    }

    const { data: tagData = {} } = data;
    return { id: tagData.id, name: tagData.name };
};

/**
 * Checks if device has reached max tag limit (6 tags per device)
 */
const hasMaxTagsForDevice = (device: any): boolean => (device?.tags?.length ?? 0) > 5;

/**
 * Filters devices that have reached max tag limit
 */
const getMaxedOutDevices = (devices: any[]): any[] => devices.filter(hasMaxTagsForDevice);

export const EditDeviceDrawer = ({ onEditDeviceCancel, selectedDevices }: Props): JSX.Element => {
    const theme: any = useTheme();
    const { t } = useTranslation();
    const { sideBarOpen: deviceSideBar } = useTypedSelector((state) => state?.common.deviceSideBar);

    const dispatch = useAppDispatch();
    const [deviceDetails, setDeviceDetails] = React.useState<any>([]);
    const [devicePayload, setDevicePayload] = React.useState<any>([]);
    const [payload, setPayload] = React.useState<any>([]);

    const [createTag, { isLoading: isTagCreating }] = useCreateTagMutation();

    const [getDeviceDetails, { isLoading: deviceDetailLoader, isFetching: deviceDetailsFetching }] =
        useLazyGetDeviceByIdQuery();

    const chipClassHandler = (tagId: any, deviceId: any): boolean => isTagNotInDevice(payload, tagId, deviceId);

    const {
        data: deviceTagsList,
        isLoading: isDeviceTagLoading,
        isFetching: isDeviceTagFetching,
    } = useGetTagsQuery<{
        data: any;
        isLoading: boolean;
        isFetching: boolean;
    }>({
        page: 0,
        size: 100,
    });
    const [updateDevicesTags] = useUpdateMultipleTagsMutation();

    /**
     * Removes a tag from a device in the payload
     */
    const payloadHandler = (tagId: string, deviceId: string): void => {
        setPayload(removeTagFromDevice(payload, tagId, deviceId));
    };

    /**
     * Merges newly selected tags with existing device payload
     */
    const handleSetTags = (): any => mergeNewTagsIntoPayload(payload, devicePayload);

    const handleSave = async (): Promise<void> => {
        const { error }: any = await updateDevicesTags(handleSetTags());
        if (!error) {
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: t('COMMON:CHANGES_SAVED_SUCCESS'),
                })
            );
            dispatch(
                setDeviceSideBar({
                    sideBarOpen: false,
                    isEnable: false,
                })
            );
            setDevicePayload([]);
            setDeviceDetails([]);
            setPayload([]);
            onEditDeviceCancel();
        }
    };

    const handleTagDelete = (data: any, index: number): void => {
        const devicePayloadClone = JSON.parse(JSON.stringify(devicePayload));
        devicePayloadClone.splice(index, 1);
        setDevicePayload(devicePayloadClone);
    };

    /**
     * Handles tag selection from autocomplete
     */
    const selectedTagsHandler = (e: any, values: any): void => {
        if (values?.length >= 20) {
            return;
        }
        setDevicePayload(values);
    };

    /**
     * Handles keyboard input in tag field
     * Creates new tags or selects existing ones based on input
     */
    const handleKeyDown = async (e: any): Promise<void> => {
        const tagValue = e.target.value;
        if (!isTagCreationKeyPress(e.code) || !canAddTag(devicePayload, tagValue)) {
            return;
        }

        const existingTag = findTagByName(getTagRecords(deviceTagsList), tagValue);
        if (existingTag) {
            setDevicePayload([...devicePayload, existingTag]);
            return;
        }

        const response: any = await createTag({ name: tagValue });
        const createdTag = getCreatedTag(response);

        if (createdTag) {
            setDevicePayload([...devicePayload, createdTag]);
        }
    };

    const renderTagChips = (device: any): JSX.Element[] =>
        device?.tags?.map((label: any) => (
            <Chip
                key={label?.id}
                deleteIcon={
                    chipClassHandler(label?.id, device?.id) ? <Add /> : <CancelIcon sx={{ color: gray[500] }} />
                }
                label={label?.name}
                disabled={chipClassHandler(label?.id, device?.id)}
                onDelete={(): void => {
                    payloadHandler(label?.id, device?.id);
                }}
            />
        )) ?? [];

    /**
     * Renders devices that have reached max tags (> 5 tags)
     */
    const renderMaxedOutDevices = (): JSX.Element | null => {
        if (deviceDetailLoader || deviceDetailsFetching) {
            return <EditDevicesSkeleton />;
        }

        const maxedDevices = getMaxedOutDevices(deviceDetails);

        if (maxedDevices.length === 0) {
            return null;
        }

        return (
            <>
                {maxedDevices.map((device: any) => (
                    <div key={device?.id}>
                        <Stack mt={2}>
                            <Typography variant="subtitle2" fontSize={'14px'} fontWeight={'600'}>
                                {device?.name ?? '--'}
                            </Typography>
                            <Typography variant="body2" fontSize={'14px'}>
                                <CustomTransComponent translationKey={'COMMON:MODEL'} /># {device?.deviceModel ?? '--'}
                            </Typography>
                            <GroupPath pathName={device?.groupPath ?? []} />
                        </Stack>
                        <Stack direction={'row'} gap={1} mt={2} flexWrap={'wrap'} pr={2}>
                            {renderTagChips(device)}
                        </Stack>
                    </div>
                ))}
            </>
        );
    };
    const tagHandler = async (id: string): Promise<void> => {
        if (deviceDetails?.findIndex((item: any) => item?.id === id) === -1) {
            const response = await getDeviceDetails(id);
            if (response?.isSuccess) {
                setDeviceDetails((prev: any) => [...prev, response?.data?.data]);
            }
        }
    };

    React.useEffect(() => {
        if (deviceSideBar) {
            selectedDevices?.forEach((selectedDevice: string) => {
                void tagHandler(selectedDevice);
            });
        } else {
            setDevicePayload([]);
        }
    }, [selectedDevices, deviceSideBar]);

    const getDeviceDetailsPayload = (): any =>
        deviceDetails?.map((item: any) => ({
            deviceId: item?.id,
            tags: item?.tags?.map?.((subItem: any) => subItem?.id),
        }));

    React.useEffect(() => {
        if (deviceDetails.length === selectedDevices?.length) {
            setPayload(getDeviceDetailsPayload);
        }
    }, [deviceDetails]);

    React.useEffect(() => {
        if (deviceDetails.length > selectedDevices?.length) {
            const deviceClone = JSON.parse(JSON.stringify(deviceDetails));
            deviceClone?.forEach((item: any, index: number) => {
                if (!selectedDevices?.includes(item?.id)) {
                    deviceClone.splice(index, 1);
                }
            });
            setDeviceDetails(deviceClone);
        }
    }, [selectedDevices, deviceDetails]);

    return (
        <React.Fragment key={'right'}>
            <Drawer
                variant="persistent"
                anchor={'right'}
                open={deviceSideBar}
                onClose={onEditDeviceCancel}
                className="custom-drawer"
            >
                <Stack justifyContent={'space-between'} className="width-368 h-100">
                    <Stack>
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            className="border-bottom-1"
                            p={2}
                        >
                            <Typography variant="subtitle1" fontSize={'16px'} fontWeight={'600'} color="primary">
                                Edit Device(s)
                            </Typography>
                            <IconButton
                                onClick={(): void => {
                                    setDeviceDetails([]);
                                    onEditDeviceCancel();
                                    setPayload([]);
                                }}
                            >
                                <Close className="black-500" />
                            </IconButton>
                        </Stack>
                        <Stack py={3} px={2}>
                            <AutoCompleteTags
                                value={devicePayload}
                                disabled={Boolean(isDeviceTagFetching) || Boolean(isDeviceTagLoading) || isTagCreating}
                                onChange={selectedTagsHandler}
                                options={
                                    deviceTagsList?.data?.records
                                        ?.filter(
                                            (item: any) =>
                                                devicePayload?.findIndex((subItem: any) => subItem?.id === item?.id) ===
                                                -1
                                        )
                                        .map((device: any) => ({
                                            name: device?.name,
                                            id: device?.id,
                                        })) ?? []
                                }
                                handleTagDelete={handleTagDelete}
                                onKeyDown={handleKeyDown}
                                placeholder="Add Tag(s)"
                            />
                            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} px={2}>
                                <FormHelperText>Max of 6 tags on each device</FormHelperText>
                                <FormHelperText>{0}/16</FormHelperText>
                            </Stack>
                            {(!deviceDetailLoader || !deviceDetailsFetching) &&
                                deviceDetails?.filter((item: any) => item?.tags?.length > 5) && (
                                    <Stack pt={3}>
                                        <Typography sx={{ fontWeight: 600 }}>{`${
                                            getMaxedOutDevices(deviceDetails)?.length
                                        } Device with maximum tags reached`}</Typography>
                                    </Stack>
                                )}
                            <Stack>{selectedDevices?.length && renderMaxedOutDevices()}</Stack>
                        </Stack>
                    </Stack>
                    <Stack
                        direction={'row'}
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        p={2}
                        className="border-top-1"
                    >
                        <Button
                            sx={{
                                border: `1px solid ${theme?.palette?.primary?.main}`,
                                '&:hover': {
                                    backgroundColor: theme?.palette?.primary?.[50],
                                },
                            }}
                            variant="outlined"
                            onClick={(): void => {
                                setPayload([]);
                                setDeviceDetails([]);
                                onEditDeviceCancel();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={
                                deviceDetailsFetching ||
                                deviceDetailLoader ||
                                (!devicePayload?.length && sumArray(payload) === sumArray(deviceDetails)) ||
                                isTagCreating
                            }
                            sx={{
                                backgroundColor: theme?.palette?.primary?.main,
                                '&:hover': {
                                    backgroundColor: theme?.palette?.primary?.main,
                                },
                            }}
                            onClick={handleSave}
                            variant="contained"
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Drawer>
        </React.Fragment>
    );
};
